import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  activateStepUpdate,
  buildInitialState,
  collapseStepUpdate,
  expandStepUpdate,
  navigateToStepUpdate,
  navigateToSubstepUpdate
} from './stepStateUpdaters';
import useStepperUrlSync from './useStepperUrlSync';

/**
 * Manages wizard steps state and navigation.
 *
 * @param {Array} initialSteps - Steps from server/props.
 * @param {Object} options - Configuration options
 * @param {boolean} options.syncWithUrl - Whether to sync navigation with URL
 * @param {string} options.baseUrl - Base URL for navigation (e.g., '/admin/practices/123/onboarding')
 */
export default function useStepper(initialSteps = [], options = {}) {
  const { syncWithUrl = false, baseUrl = '', skipInitialUrlSync = false } = options;
  const [stepsState, setStepsState] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const isNavigatingProgrammatically = useRef(false);

  const replaceWithInitial = useCallback((incoming) => {
    setStepsState((prev) => buildInitialState(incoming, prev));
  }, []);

  useEffect(() => {
    replaceWithInitial(initialSteps);
    if (initialSteps && initialSteps.length > 0) {
      setIsInitialized(true);
    }
  }, [initialSteps, replaceWithInitial]);

  const flattenedSubsteps = useMemo(
    () =>
      stepsState.flatMap((step) =>
        (step.substeps || []).map((sub) => ({
          ...sub,
          stepCode: step.code
        }))
      ),
    [stepsState]
  );

  const activeSubstep = useMemo(() => flattenedSubsteps.find((s) => s.active), [flattenedSubsteps]);

  const activeStep = useMemo(
    () =>
      stepsState.find((s) => s.active) ||
      stepsState.find((s) => s.substeps?.some((sub) => sub.active)),
    [stepsState]
  );

  const expandStep = useCallback((stepCode) => {
    setStepsState((prev) => expandStepUpdate(prev, stepCode));
  }, []);

  const collapseStep = useCallback((stepCode) => {
    setStepsState((prev) => collapseStepUpdate(prev, stepCode));
  }, []);

  const activateStep = useCallback((stepCode, { fromUrl = false } = {}) => {
    if (!fromUrl) {
      isNavigatingProgrammatically.current = true;
    }
    setStepsState((prev) => activateStepUpdate(prev, stepCode));
  }, []);

  const navigateToSubstep = useCallback((substepCode, { fromUrl = false } = {}) => {
    if (!fromUrl) {
      isNavigatingProgrammatically.current = true;
    }
    setStepsState((prev) => navigateToSubstepUpdate(prev, substepCode));
  }, []);

  const navigateTo = useCallback(
    ({ stepCode, substepCode } = {}) => {
      if (substepCode) {
        navigateToSubstep(substepCode);
        return;
      }
      if (!stepCode) return;
      setStepsState((prev) => navigateToStepUpdate(prev, stepCode));
    },
    [navigateToSubstep]
  );

  const goNext = useCallback(() => {
    if (!flattenedSubsteps.length) return;
    const idx = flattenedSubsteps.findIndex((s) => s.active);
    if (idx > -1 && idx < flattenedSubsteps.length - 1) {
      navigateToSubstep(flattenedSubsteps[idx + 1].code);
    }
  }, [flattenedSubsteps, navigateToSubstep]);

  const goPrev = useCallback(() => {
    if (!flattenedSubsteps.length) return undefined;
    const idx = flattenedSubsteps.findIndex((s) => s.active);
    if (idx === 0) return undefined;
    if (idx > 0) {
      navigateToSubstep(flattenedSubsteps[idx - 1].code);
    }
  }, [flattenedSubsteps, navigateToSubstep]);

  const canGoPrev = useMemo(
    () => flattenedSubsteps.findIndex((s) => s.active) > 0,
    [flattenedSubsteps]
  );

  useStepperUrlSync({
    syncWithUrl,
    baseUrl,
    skipInitialUrlSync,
    isInitialized,
    stepsState,
    flattenedSubsteps,
    activeStep,
    activeSubstep,
    activateStep,
    navigateToSubstep,
    isNavigatingProgrammatically
  });

  return {
    stepsState,
    setStepsState,
    activeStep,
    activeSubstep,
    flattenedSubsteps,
    replaceWithInitial,
    expandStep,
    collapseStep,
    activateStep,
    navigateTo,
    navigateToSubstep,
    goNext,
    goPrev,
    canGoPrev
  };
}
